import { useEffect, useState } from "react";
import { Button, Row, TextInput } from "@narmi/design_system";
import { Dialog } from "cerulean";
import { useLocalization } from "@fluent/react";

export type DualApprovalRejectApproveDialogType = {
  isOpen: boolean;
  type: "" | "approve" | "reject";
  handleClose: () => void;
  handleReject: (note?: string) => void;
  handleApprove: () => void;
  totalSelected: number;
  variant?: "payment" | "template";
  hasNoActiveVersion?: boolean;
};

const DualApprovalRejectApproveDialog = ({
  isOpen,
  type,
  handleClose,
  handleReject,
  handleApprove,
  totalSelected,
  variant = "payment",
  hasNoActiveVersion = false,
}: DualApprovalRejectApproveDialogType) => {
  const { l10n } = useLocalization();
  const rejectModal = type === "reject";
  const prefix = variant === "template" ? "template-" : "";
  const requiresRejectReason = rejectModal && variant === "template";
  const [rejectionReason, setRejectionReason] = useState("");

  const rejectContent =
    variant === "template" && hasNoActiveVersion
      ? l10n.getString("template-reject-modal-content-no-active-version", {
          totalSelected,
        })
      : l10n.getString(`${prefix}reject-modal-content`, { totalSelected });

  useEffect(() => {
    if (isOpen) {
      setRejectionReason("");
    }
  }, [isOpen, type]);

  const handleClickRejectOrApprove = () => {
    if (type === "reject") {
      const trimmedReason = rejectionReason.trim();
      if (requiresRejectReason && !trimmedReason) {
        return;
      }
      handleReject(trimmedReason || "");
    } else if (type === "approve") {
      handleApprove();
    }
    handleClose();
  };

  return (
    <Dialog
      width="475px"
      isOpen={isOpen}
      onUserDismiss={handleClose}
      title={
        rejectModal
          ? l10n.getString(`${prefix}reject-modal-heading`, { totalSelected })
          : l10n.getString(`${prefix}approve-modal-heading`, { totalSelected })
      }
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="plain"
              onClick={handleClose}
              testId="cancel-reject-approve-modal"
            >
              Cancel
            </Button>
          </Row.Item>
          <Row.Item shrink>
            <Button
              kind="primary"
              onClick={handleClickRejectOrApprove}
              testId="confirm-reject-approve-modal"
              disabled={requiresRejectReason && !rejectionReason.trim()}
            >
              {rejectModal
                ? l10n.getString(
                    variant === "template"
                      ? "template-reject-modal-confirm"
                      : "reject-modal-confirm",
                    { totalSelected }
                  )
                : l10n.getString("approve-modal-confirm")}
            </Button>
          </Row.Item>
        </Row>
      }
    >
      <div className="margin--bottom--s">
        {rejectModal
          ? rejectContent
          : l10n.getString(`${prefix}approve-modal-content`, { totalSelected })}
      </div>
      {requiresRejectReason ? (
        <TextInput
          label={l10n.getString("template-reject-reason-label")}
          multiline={false}
          value={rejectionReason}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setRejectionReason(event.target.value);
          }}
          testId="template-reject-reason-input"
        />
      ) : null}
    </Dialog>
  );
};

export default DualApprovalRejectApproveDialog;
